import { useState, useMemo } from 'react';
import BackendLayout from '../../components/feature/BackendLayout';
import { mockProducts } from '../../mocks/products';
import ProductFormModal from './components/ProductFormModal';
import ImportModal from './components/ImportModal';
import BulkEditModal from './components/BulkEditModal';

type Product = typeof mockProducts[0];

export default function ProductsManagementPage() {
  const [products, setProducts] = useState<Product[]>(mockProducts);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const categories = ['All', ...Array.from(new Set(products.map(p => p.category)))];

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || p.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const sortedProducts = useMemo(() => {
    if (!sortColumn) return filteredProducts;
    return [...filteredProducts].sort((a, b) => {
      let valA: any;
      let valB: any;
      switch (sortColumn) {
        case 'name':
          valA = a.name.toLowerCase();
          valB = b.name.toLowerCase();
          break;
        case 'category':
          valA = a.category.toLowerCase();
          valB = b.category.toLowerCase();
          break;
        case 'pricing':
          valA = a.pricing.toLowerCase();
          valB = b.pricing.toLowerCase();
          break;
        case 'rating':
          valA = a.rating;
          valB = b.rating;
          break;
        case 'reviews':
          valA = a.reviews;
          valB = b.reviews;
          break;
        case 'commission':
          valA = parseFloat(a.commission) || 0;
          valB = parseFloat(b.commission) || 0;
          break;
        default:
          return 0;
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredProducts, sortColumn, sortDirection]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ column }: { column: string }) => (
    <span className="inline-flex flex-col ml-1 -space-y-1.5 align-middle">
      <i className={`ri-arrow-up-s-fill text-xs leading-none ${sortColumn === column && sortDirection === 'asc' ? 'text-teal-600' : 'text-gray-300'}`}></i>
      <i className={`ri-arrow-down-s-fill text-xs leading-none ${sortColumn === column && sortDirection === 'desc' ? 'text-teal-600' : 'text-gray-300'}`}></i>
    </span>
  );

  const allFilteredSelected = sortedProducts.length > 0 && sortedProducts.every(p => selectedIds.has(p.id));
  const someFilteredSelected = sortedProducts.some(p => selectedIds.has(p.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      const newSet = new Set(selectedIds);
      sortedProducts.forEach(p => newSet.delete(p.id));
      setSelectedIds(newSet);
    } else {
      const newSet = new Set(selectedIds);
      sortedProducts.forEach(p => newSet.add(p.id));
      setSelectedIds(newSet);
    }
  };

  const toggleSelectOne = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleAddProduct = (productData: Omit<Product, 'id'>) => {
    const newProduct: Product = {
      ...productData,
      id: `product-${Date.now()}`,
    };
    setProducts([newProduct, ...products]);
    setIsModalOpen(false);
  };

  const handleEditProduct = (productData: Omit<Product, 'id'>) => {
    if (!editingProduct) return;
    setProducts(products.map(p => 
      p.id === editingProduct.id ? { ...productData, id: editingProduct.id } : p
    ));
    setEditingProduct(null);
    setIsModalOpen(false);
  };

  const handleDeleteProduct = (id: string) => {
    if (confirm('Are you sure you want to delete this product?')) {
      setProducts(products.filter(p => p.id !== id));
      const newSet = new Set(selectedIds);
      newSet.delete(id);
      setSelectedIds(newSet);
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    if (confirm(`Are you sure you want to delete ${selectedIds.size} product${selectedIds.size !== 1 ? 's' : ''}? This cannot be undone.`)) {
      setProducts(products.filter(p => !selectedIds.has(p.id)));
      setSelectedIds(new Set());
    }
  };

  const handleBulkEdit = (changes: Partial<Omit<Product, 'id'>>) => {
    const tagMode = (changes as any)._tagMode as 'add' | 'replace' | undefined;
    const newTags = (changes as any)._tags as string[] | undefined;
    delete (changes as any)._tagMode;
    delete (changes as any)._tags;

    setProducts(products.map(p => {
      if (!selectedIds.has(p.id)) return p;
      const updated = { ...p, ...changes };
      if (newTags !== undefined) {
        if (tagMode === 'replace') {
          updated.tags = [...newTags];
        } else {
          const merged = new Set([...p.tags, ...newTags]);
          updated.tags = Array.from(merged);
        }
      }
      return updated;
    }));
    setIsBulkEditOpen(false);
    setSelectedIds(new Set());
  };

  const handleImportProducts = (importedProducts: Omit<Product, 'id'>[]) => {
    const newProducts = importedProducts.map((p, index) => ({
      ...p,
      id: `product-${Date.now()}-${index}`,
    }));
    setProducts([...newProducts, ...products]);
    setIsImportModalOpen(false);
  };

  const openEditModal = (product: Product) => {
    setEditingProduct(product);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
  };

  return (
    <BackendLayout>
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold text-gray-900">Products Management</h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsImportModalOpen(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-gray-700 font-semibold rounded-lg border-2 border-gray-200 hover:border-teal-500 hover:text-teal-600 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-upload-2-line text-lg"></i>
              Import from CSV
            </button>
            <button
              onClick={() => {
                setEditingProduct(null);
                setIsModalOpen(true);
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-500 text-white font-semibold rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line text-lg"></i>
              Add New Product
            </button>
          </div>
        </div>
        <p className="text-gray-600 text-sm">Manage all products displayed on your site</p>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg px-5 py-3 mb-6 flex items-center justify-between animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-teal-500 rounded-full">
              <span className="text-white text-xs font-bold">{selectedIds.size}</span>
            </div>
            <span className="text-sm font-semibold text-teal-800">
              {selectedIds.size} product{selectedIds.size !== 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsBulkEditOpen(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-white text-teal-700 text-sm font-semibold rounded-md border border-teal-300 hover:bg-teal-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-line text-base"></i>
              Bulk Edit
            </button>
            <button
              onClick={handleBulkDelete}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-white text-red-600 text-sm font-semibold rounded-md border border-red-200 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-line text-base"></i>
              Delete Selected
            </button>
            <button
              onClick={clearSelection}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-gray-500 text-sm font-medium hover:text-gray-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line text-base"></i>
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                <i className="ri-search-line text-gray-400"></i>
              </div>
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-neon"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600 text-sm font-medium whitespace-nowrap">Category:</span>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 text-sm text-gray-900 focus:outline-none focus:border-neon cursor-pointer"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-neon/10">
              <i className="ri-shopping-bag-line text-neon text-lg"></i>
            </div>
            <div>
              <p className="text-gray-600 text-xs font-medium">Total Products</p>
              <p className="text-2xl font-bold text-gray-900">{products.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-teal-50">
              <i className="ri-folder-line text-teal-600 text-lg"></i>
            </div>
            <div>
              <p className="text-gray-600 text-xs font-medium">Categories</p>
              <p className="text-2xl font-bold text-gray-900">{categories.length - 1}</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-yellow-50">
              <i className="ri-star-line text-yellow-600 text-lg"></i>
            </div>
            <div>
              <p className="text-gray-600 text-xs font-medium">Avg Rating</p>
              <p className="text-2xl font-bold text-gray-900">
                {(products.reduce((sum, p) => sum + p.rating, 0) / products.length).toFixed(1)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-green-50">
              <i className="ri-eye-line text-green-600 text-lg"></i>
            </div>
            <div>
              <p className="text-gray-600 text-xs font-medium">Total Reviews</p>
              <p className="text-2xl font-bold text-gray-900">
                {products.reduce((sum, p) => sum + p.reviews, 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left w-12">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      ref={(el) => { if (el) el.indeterminate = someFilteredSelected && !allFilteredSelected; }}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 accent-teal-500 cursor-pointer"
                    />
                  </label>
                </th>
                <th
                  onClick={() => handleSort('name')}
                  className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:text-teal-600 select-none transition-colors"
                >
                  Product<SortIcon column="name" />
                </th>
                <th
                  onClick={() => handleSort('category')}
                  className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:text-teal-600 select-none transition-colors"
                >
                  Category<SortIcon column="category" />
                </th>
                <th
                  onClick={() => handleSort('pricing')}
                  className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:text-teal-600 select-none transition-colors"
                >
                  Pricing<SortIcon column="pricing" />
                </th>
                <th
                  onClick={() => handleSort('rating')}
                  className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:text-teal-600 select-none transition-colors"
                >
                  Rating<SortIcon column="rating" />
                </th>
                <th
                  onClick={() => handleSort('reviews')}
                  className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:text-teal-600 select-none transition-colors"
                >
                  Reviews<SortIcon column="reviews" />
                </th>
                <th
                  onClick={() => handleSort('commission')}
                  className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:text-teal-600 select-none transition-colors"
                >
                  Commission<SortIcon column="commission" />
                </th>
                <th className="px-6 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedProducts.length > 0 ? (
                sortedProducts.map((product) => (
                  <tr
                    key={product.id}
                    className={`transition-colors ${selectedIds.has(product.id) ? 'bg-teal-50/50' : 'hover:bg-gray-50'}`}
                  >
                    <td className="px-4 py-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(product.id)}
                          onChange={() => toggleSelectOne(product.id)}
                          className="w-4 h-4 accent-teal-500 cursor-pointer"
                        />
                      </label>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 overflow-hidden flex-shrink-0 bg-gray-100">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">{product.name}</p>
                          <p className="text-xs text-gray-500 line-clamp-1">{product.description}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium">
                        {product.category}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-bold text-gray-900">{product.pricing}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <i className="ri-star-fill text-yellow-500 text-sm"></i>
                        <span className="text-sm font-bold text-gray-900">{product.rating}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">{product.reviews.toLocaleString()}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-bold text-neon">{product.commission}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(product)}
                          className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 transition-colors cursor-pointer"
                          title="Edit product"
                        >
                          <i className="ri-edit-line text-base"></i>
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product.id)}
                          className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                          title="Delete product"
                        >
                          <i className="ri-delete-bin-line text-base"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-gray-100">
                      <i className="ri-search-line text-2xl text-gray-400"></i>
                    </div>
                    <p className="text-gray-500 text-sm">No products found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {isModalOpen && (
        <ProductFormModal
          product={editingProduct}
          onSave={editingProduct ? handleEditProduct : handleAddProduct}
          onClose={closeModal}
        />
      )}

      {isImportModalOpen && (
        <ImportModal
          onClose={() => setIsImportModalOpen(false)}
          onImport={handleImportProducts}
        />
      )}

      {isBulkEditOpen && (
        <BulkEditModal
          selectedCount={selectedIds.size}
          onApply={handleBulkEdit}
          onClose={() => setIsBulkEditOpen(false)}
        />
      )}
    </BackendLayout>
  );
}
